<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGarageStore } from '@/stores/garage.js'
import { useModalStore } from '@/stores/modal.js'
import { useRequestAQuoteStore } from '@/stores/request_a_quote.js'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'

const props = defineProps({
  service: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  serviceIndex: {
    type: Number,
    required: true
  },
  optionChoice: {
    type: Object,
    required: true
  },
  isCategoryOptionsLoading: {
    type: Boolean,
    required: true
  },
  categoryOptions: {
    type: Array,
    required: true
  }
})

// Stores
const garageStore = useGarageStore()
const modalStore = useModalStore()
const blitzQuoteStore = useRequestAQuoteStore()

// Variables
const { t } = useI18n()

const categoryOptionList = computed(() => {
  // Map current categoryOption selections user made
  const selectedOptions = props.service.optionChoices.map(option => option.category_option);

  // Determine available categoryOptions
  return props.categoryOptions.filter(option => {
    // Include categoryOption if it's the current selected option (based on index) or not in selected options
    return (!selectedOptions.includes(option) || props.service.optionChoices[props.index].category_option === option);
  });
});

// Queries
const categoryOptionChoicesUrl = computed(() => {
  const url = new URL(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/category_option_choices`)
  url.searchParams.append('query[category_option_id_eq]', props.optionChoice.category_option.id)
  url.searchParams.append('query[archived_eq]', 'false')
  return url.href;
})
const {
  isFetching: isCategoryOptionChoicesLoading,
  data: categoryOptionChoices
} = useIntelichekFetch(categoryOptionChoicesUrl, { refetch: true }).json()

// Events
watch(() => props.optionChoice.category_option, async () => {
  props.optionChoice.category_option_choice = t('request_a_quote.service_section.choice_placeholder')
})

// Functions
function removeOptionChoice() {
  props.service.optionChoices.splice(props.index, 1)
}

</script>

<template>
  <div class="col-span-1 w-full">
    <div
      v-if="props.service.category && props.service.category !== t('request_a_quote.service_section.service_category_placeholder')"
      class="w-full mb-2 md:mb-0 mt-2">
      <div class="flex items-center justify-between">
        <label :for="'category_option_dropdown-' + props.index"
               class="block text-gray-600 text-sm font-semibold">{{ t('request_a_quote.service_section.options_label', { option: props.service.category.name })
          }}</label>
        <p v-if="index !== 0" class="block remove-option text-sm font-semibold cursor-pointer"
           @click="removeOptionChoice">{{ t('request_a_quote.service_section.remove') }}</p>
      </div>
      <div class="relative">
        <select
          v-if="!props.isCategoryOptionsLoading"
          :id="'category_option_dropdown-' + props.index"
          v-model="props.optionChoice.category_option"
          class="appearance-none w-full mt-1 p-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300">
          <option :value="t('request_a_quote.service_section.option_placeholder')" disabled>
            {{ t('request_a_quote.service_section.option_placeholder') }}
          </option>
          <option v-for="categoryOption in categoryOptionList" :key="categoryOption.id" :value="categoryOption">
            {{ categoryOption.option_name }}
          </option>
        </select>
        <div v-else
             class="block appearance-none w-full mt-1 p-2 px-4 rounded-md h-[40px] border border-gray-300 py-2 px-4 pr-8 leading-tight loading-input cursor-not-allowed">
          <div class="flex justify-center items-center">
            <svg class="animate-spin -ml-1 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="ml-2 text-gray-500">{{ `${t('my_garage.loading')}...` }}</span>
          </div>
        </div>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <img src="../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
        </div>
      </div>
    </div>

    <div
      v-if="categoryOptionChoices && props.optionChoice.category_option && props.optionChoice.category_option !== t('request_a_quote.service_section.option_placeholder')"
      class="w-full mb-2 md:mb-0 mt-4">
      <label :for="'category_option_choice_dropdown-' + props.index"
             class="block text-gray-600 text-sm font-semibold">
        {{ t('request_a_quote.service_section.choices_label', { choice: props.optionChoice.category_option.option_name }) }}
      </label>
      <div class="relative">
        <select
          v-if="!isCategoryOptionChoicesLoading"
          :id="'category_option_choice_dropdown-' + props.index"
          v-model="props.optionChoice.category_option_choice"
          class="appearance-none w-full mt-1 p-2 px-4 rounded-md border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300"
        >
          <option :value="t('request_a_quote.service_section.choice_placeholder')" disabled>
            {{ t('request_a_quote.service_section.choice_placeholder') }}
          </option>
          <option v-for="categoryOptionChoice in categoryOptionChoices.data" :key="categoryOptionChoice.id"
                  :value="categoryOptionChoice">{{ categoryOptionChoice.choice_name }}
          </option>
        </select>
        <div v-else
             class="block appearance-none w-full mt-1 p-2 px-4 rounded-md h-[40px] border border-gray-300 py-2 px-4 pr-8 leading-tight loading-input cursor-not-allowed">
          <div class="flex justify-center items-center">
            <svg class="animate-spin -ml-1 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="ml-2 text-gray-500">{{ `${t('my_garage.loading')}...` }}</span>
          </div>
        </div>
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <img src="../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
        </div>
      </div>
    </div>

    <div class="flex flex-row items-center mt-4 justify-center"
         v-if="props.service.optionChoices.length < 3 && props.index === props.service.optionChoices.length - 1 && (props.optionChoice.category_option && props.optionChoice.category_option !== t('request_a_quote.service_section.option_placeholder')) && (props.optionChoice.category_option_choice && props.optionChoice.category_option_choice !== t('request_a_quote.service_section.choice_placeholder'))">
      <div class="flex justify-center cursor-pointer" @click="blitzQuoteStore.addOptionSet(props.serviceIndex - 1)">
        <img
          src="../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1"
          class="cursor-pointer mr-2 h-5">
        <button
          type="button"
          class="add-service text-sm font-medium inline-block select-vehicle">
          {{ t('request_a_quote.service_section.add_option_set') }}
        </button>
      </div>
    </div>

    <div v-if="props.index !== props.service.optionChoices.length - 1" class="mt-4 mx-4 border-t md:border-t-0"></div>

  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/request_a_quote/request_a_quote';
</style>